/**
 * NeighborhoodModal CSS Styles
 *
 * CSS-in-JS styles injected once on first component instantiation.
 * Scoped via .sm-neighborhood-modal prefix.
 */
let stylesInjected = false;
const STYLES = `
/* Modal Overlay */
.sm-neighborhood-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
}

.sm-neighborhood-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Modal Container */
.sm-neighborhood-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: translateY(20px);
  transition: transform 0.3s;
  overflow: hidden;
}

.sm-neighborhood-overlay.active .sm-neighborhood-modal {
  transform: translateY(0);
}

/* Fixed Header */
.sm-neighborhood-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: white;
  flex-shrink: 0;
  gap: 16px;
}

.sm-neighborhood-header-left {
  flex: 1;
  min-width: 0;
}

.sm-neighborhood-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f2937;
}

.sm-neighborhood-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.875rem;
  color: #666;
}

.sm-neighborhood-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sm-neighborhood-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sm-neighborhood-close:hover {
  background: #e0e0e0;
}

/* Action Buttons (Favorite, Alert, Share) */
.sm-neighborhood-action-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
  transition: background 0.15s, color 0.15s;
}

.sm-neighborhood-action-btn:hover {
  background: #e0e0e0;
  color: #374151;
}

.sm-neighborhood-action-btn svg {
  width: 16px;
  height: 16px;
}

/* Favorite button - red when active */
.sm-neighborhood-favorite.active {
  background: #fee2e2;
  color: #dc2626;
}

.sm-neighborhood-favorite.active:hover {
  background: #fecaca;
  color: #b91c1c;
}

/* Alert button - amber-tinted at rest too (not just once active), so it
   reads as a distinct, inviting action rather than a neutral utility icon.
   Active/subscribed state inverts to a solid filled bell - see
   PropertyModal's identical treatment for the full rationale. */
.sm-neighborhood-alert {
  background: #fef3c7;
  color: #d97706;
}

.sm-neighborhood-alert:hover {
  background: #fde68a;
  color: #b45309;
}

.sm-neighborhood-alert.active {
  background: #f59e0b;
  color: #ffffff;
}

.sm-neighborhood-alert.active:hover {
  background: #d97706;
  color: #ffffff;
}

/* Labeled pill shape (icon + visible text) - see PropertyModal's identical
   treatment for rationale. */
.sm-neighborhood-alert-labeled {
  width: auto;
  border-radius: 16px;
  padding: 0 12px 0 10px;
  gap: 5px;
}

.sm-neighborhood-alert-labeled svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.sm-neighborhood-alert-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Agent Pick button (apps/agent's Compile mode) - see PropertyModal's
   identical treatment for the checkbox-vs-circle rationale. */
.sm-neighborhood-agent-pick {
  border-radius: 8px;
  background: #ffedd5;
  color: #ea580c;
}

.sm-neighborhood-agent-pick:hover {
  background: #fed7aa;
  color: #c2410c;
}

.sm-neighborhood-agent-pick.active {
  background: #f97316;
  color: #ffffff;
}

.sm-neighborhood-agent-pick.active:hover {
  background: #ea580c;
  color: #ffffff;
}

/* Tab Navigation */
.sm-neighborhood-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
  padding: 0 20px;
}

.sm-neighborhood-tab {
  padding: 12px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.sm-neighborhood-tab:hover {
  color: #374151;
}

.sm-neighborhood-tab.active {
  color: #2c5530;
  border-bottom-color: #2c5530;
}

/* Content tab is an agent-only function (see NeighborhoodModalOptions'
   onLoadAgentNote/onSaveAgentNote) - orange instead of the other tabs'
   green so it visibly stands out as an agent addition, not just another
   map tab. Must come after .sm-neighborhood-tab.active to win on
   specificity (both are single-class selectors - source order decides). */
.sm-neighborhood-tab[data-tab="content"].active {
  color: #f97316;
  border-bottom-color: #f97316;
}

/* Modal Body */
.sm-neighborhood-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1 1 0;
  min-height: 0;
}

/* Tab Content */
.sm-neighborhood-tab-content {
  display: none;
}

.sm-neighborhood-tab-content.active {
  display: block;
}

/* Stats Row - up to 6 tiles now (Units/For Sale/Year Built/STR Allowed plus
   Walk Score/Schools), so auto-fit + a smaller minimum lets 5-6 sit on one
   row instead of wrapping 1-2 tiles onto an awkward near-empty second row. */
.sm-neighborhood-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.sm-neighborhood-stat {
  text-align: center;
  padding: 10px 6px;
  background: #f9fafb;
  border-radius: 8px;
}

.sm-neighborhood-stat-value {
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
}

.sm-neighborhood-stat-label {
  font-size: 0.7rem;
  color: #666;
  margin-top: 2px;
}

/* Image Carousel */
.sm-neighborhood-gallery {
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.sm-neighborhood-gallery-main {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: #f0f0f0;
}

.sm-neighborhood-gallery-main img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sm-neighborhood-gallery-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e0f2e9 0%, #c7e5d8 100%);
  color: #2c5530;
  font-size: 0.9rem;
}

.sm-neighborhood-gallery-map {
  width: 100%;
  height: 100%;
  background: #f0f0f0;
}

.sm-neighborhood-gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sm-neighborhood-gallery-nav:hover {
  background: rgba(0, 0, 0, 0.7);
}

.sm-neighborhood-gallery-prev {
  left: 10px;
}

.sm-neighborhood-gallery-next {
  right: 10px;
}

.sm-neighborhood-gallery-counter {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
}

.sm-neighborhood-gallery-thumbs {
  display: flex;
  gap: 6px;
  padding: 8px 0;
  overflow-x: auto;
}

.sm-neighborhood-gallery-thumb {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.sm-neighborhood-gallery-thumb:hover,
.sm-neighborhood-gallery-thumb.active {
  opacity: 1;
}

/* Description */
.sm-neighborhood-description {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.sm-neighborhood-description p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #4b5563;
}

/* Collapsible Sections */
.sm-neighborhood-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.sm-neighborhood-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  font-family: inherit;
}

.sm-neighborhood-section-header:hover {
  background: #f3f4f6;
}

/* Bigger than the drawer chevrons this matches (18px vs 10px) and a longer,
   springy "overshoot and settle" ease so the flip itself draws the eye,
   not just the end position - unscoped so it applies wherever this class
   is used (both accordion variants on this modal share it). */
.sm-neighborhood-section-chevron {
  flex-shrink: 0;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.sm-neighborhood-section.collapsed .sm-neighborhood-section-chevron {
  transform: rotate(-90deg);
}

.sm-neighborhood-section.collapsed .sm-neighborhood-section-content {
  display: none;
}

.sm-neighborhood-section-content {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}

/* Detail Row */
.sm-neighborhood-detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.sm-neighborhood-detail-row:last-child {
  border-bottom: none;
}

.sm-neighborhood-detail-label {
  color: #666;
  font-size: 0.9rem;
}

.sm-neighborhood-detail-value {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.sm-neighborhood-detail-value a {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: #999;
}

.sm-neighborhood-detail-value a:hover {
  color: #2c5530;
  text-decoration-color: #2c5530;
}

/* Amenities Grid */
.sm-neighborhood-amenities-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sm-neighborhood-amenity {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f0fdf4;
  color: #166534;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.sm-neighborhood-amenity-icon {
  font-size: 1rem;
}

/* For Sale Tab - Listings */
.sm-neighborhood-listings {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sm-neighborhood-listing-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.sm-neighborhood-listing-card:hover {
  background: #f0fdf4;
  border-color: #22c55e;
}

.sm-neighborhood-listing-image {
  width: 100px;
  height: 75px;
  border-radius: 6px;
  object-fit: cover;
  background: #e5e7eb;
  flex-shrink: 0;
}

.sm-neighborhood-listing-image-placeholder {
  width: 100px;
  height: 75px;
  border-radius: 6px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  flex-shrink: 0;
}

.sm-neighborhood-listing-info {
  flex: 1;
  min-width: 0;
}

.sm-neighborhood-listing-price {
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 4px;
  font-size: 1rem;
}

.sm-neighborhood-listing-status-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.sm-neighborhood-listing-status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.sm-neighborhood-listing-address {
  font-size: 0.85rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.sm-neighborhood-listing-stats {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #666;
}

.sm-neighborhood-listing-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

/* Sales Data Tab */
.sm-neighborhood-sales-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.sm-neighborhood-sales-stat {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.sm-neighborhood-sales-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.sm-neighborhood-sales-stat-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 4px;
}

.sm-neighborhood-sales-trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  margin-left: 8px;
}

.sm-neighborhood-sales-trend.up {
  color: #16a34a;
}

.sm-neighborhood-sales-trend.down {
  color: #dc2626;
}

.sm-neighborhood-sales-trend.stable {
  color: #6b7280;
}

.sm-neighborhood-sales-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

/* View on Map button */
.sm-neighborhood-view-map-btn {
  width: 100%;
  padding: 12px 20px;
  background: #2c5530;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;
  transition: background 0.2s;
  font-family: inherit;
}

.sm-neighborhood-view-map-btn:hover {
  background: #1e3d22;
}

/* Content tab (Agent Insight - agent-only, see NeighborhoodModalOptions'
   onLoadAgentNote/onSaveAgentNote) */
.sm-neighborhood-content-tab-hint {
  font-size: 0.85rem;
  color: #666;
  margin: 0 0 12px;
  line-height: 1.4;
}

.sm-neighborhood-note-status-row {
  margin: 0 0 10px;
  min-height: 20px;
}

.sm-neighborhood-note-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.sm-neighborhood-note-badge-pending { background: #fef3c7; color: #92400e; }
.sm-neighborhood-note-badge-approved { background: #dcfce7; color: #166534; }
.sm-neighborhood-note-badge-rejected { background: #fee2e2; color: #991b1b; }

.sm-neighborhood-note-rejection-reason {
  margin-left: 8px;
  font-size: 0.8rem;
  color: #991b1b;
}

.sm-neighborhood-content-textarea {
  width: 100%;
  min-height: 160px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1f2937;
  resize: vertical;
  box-sizing: border-box;
}

.sm-neighborhood-content-textarea:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15);
}

.sm-neighborhood-content-tab-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.sm-neighborhood-content-tab-status {
  font-size: 0.8rem;
  color: #666;
}

.sm-neighborhood-content-save-btn {
  padding: 8px 20px;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}

.sm-neighborhood-content-save-btn:hover {
  background: #ea580c;
}

.sm-neighborhood-content-save-btn:disabled {
  background: #9ca3af;
  cursor: default;
}

/* Loading state */
.sm-neighborhood-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.sm-neighborhood-loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #2c5530;
  border-radius: 50%;
  animation: sm-neighborhood-spin 0.8s linear infinite;
  margin-right: 12px;
}

@keyframes sm-neighborhood-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .sm-neighborhood-modal {
    width: 95%;
    height: 95vh;
    max-height: 95vh;
  }

  .sm-neighborhood-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .sm-neighborhood-sales-stats {
    grid-template-columns: 1fr;
  }

  .sm-neighborhood-gallery-nav {
    display: none;
  }

  .sm-neighborhood-header {
    padding: 12px 16px;
  }

  .sm-neighborhood-title {
    font-size: 1.2rem;
  }

  .sm-neighborhood-close,
  .sm-neighborhood-share {
    width: 36px;
    height: 36px;
  }

  .sm-neighborhood-tabs {
    padding: 0 12px;
  }

  .sm-neighborhood-tab {
    padding: 10px 12px;
    font-size: 0.85rem;
  }
}

/* Small mobile: full screen */
@media (max-width: 480px) {
  .sm-neighborhood-modal {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .sm-neighborhood-tab-content {
    padding: 12px;
  }

  .sm-neighborhood-listing-card {
    padding: 10px;
  }

  .sm-neighborhood-view-map-btn {
    padding: 12px 20px;
    font-size: 14px;
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .sm-neighborhood-close,
  .sm-neighborhood-share {
    min-width: 44px;
    min-height: 44px;
  }

  .sm-neighborhood-tab {
    min-height: 44px;
  }

  .sm-neighborhood-view-map-btn {
    min-height: 48px;
  }

  .sm-neighborhood-listing-card {
    min-height: 72px;
  }

  .sm-neighborhood-gallery-dot {
    width: 10px;
    height: 10px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .sm-neighborhood-modal {
    width: 98%;
    max-width: 100%;
    max-height: 98vh;
  }
}

/* Share Toast */
.sm-share-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(100px);
  background: #1f2937;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10000;
  opacity: 0;
  transition: transform 0.3s, opacity 0.3s;
}

.sm-share-toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sm-toast-action {
  flex-shrink: 0;
  padding: 5px 12px;
  background: #f59e0b;
  color: #1f2937;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.sm-toast-action:hover {
  background: #fbbf24;
}

.sm-share-toast.show {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

/* AI Content Sections */
.sm-neighborhood-content-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.sm-neighborhood-content-section .sm-neighborhood-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  text-align: left;
}

.sm-neighborhood-content-section .sm-neighborhood-section-header:hover {
  background: #f3f4f6;
}

.sm-neighborhood-content-section.collapsed .sm-neighborhood-section-chevron {
  transform: rotate(-90deg);
}

.sm-neighborhood-content-section.collapsed .sm-neighborhood-section-content {
  display: none;
}

.sm-neighborhood-content-section .sm-neighborhood-section-content {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.sm-neighborhood-content-html {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #374151;
}

.sm-neighborhood-content-html p {
  margin: 0 0 1em 0;
}

.sm-neighborhood-content-html p:last-child {
  margin-bottom: 0;
}

.sm-neighborhood-content-html ul,
.sm-neighborhood-content-html ol {
  margin: 0 0 1em 0;
  padding-left: 1.5em;
}

.sm-neighborhood-content-html li {
  margin-bottom: 0.5em;
}

.sm-neighborhood-content-html a {
  color: #2563eb;
  text-decoration: none;
}

.sm-neighborhood-content-html a:hover {
  text-decoration: underline;
}

.sm-neighborhood-content-html h3,
.sm-neighborhood-content-html h4 {
  margin: 1em 0 0.5em 0;
  font-weight: 600;
  color: #1f2937;
}

.sm-agent-insights {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.sm-agent-insights-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.sm-agent-insight {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}

.sm-agent-insight:last-child {
  margin-bottom: 0;
}

.sm-agent-insight-body {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #374151;
}

.sm-agent-insight-body p {
  margin: 0 0 0.5em 0;
}

.sm-agent-insight-body p:last-child {
  margin-bottom: 0;
}

.sm-agent-insight-byline {
  display: block;
  margin-top: 8px;
  font-size: 0.75rem;
  color: #6b7280;
  text-decoration: none;
}

.sm-agent-insight-byline:hover {
  text-decoration: underline;
  color: #2563eb;
}

.sm-neighborhood-content-html h3:first-child,
.sm-neighborhood-content-html h4:first-child {
  margin-top: 0;
}

.sm-neighborhood-schools-note {
  display: block;
  margin-top: 8px;
  font-size: 0.75rem;
  color: #666;
}

/* Disclaimers in Neighborhood Modal */
#sm-neighborhood-disclaimers {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}
`;
export function injectStyles() {
    if (stylesInjected)
        return;
    const styleEl = document.createElement('style');
    styleEl.id = 'sm-neighborhood-modal-styles';
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    stylesInjected = true;
}
//# sourceMappingURL=styles.js.map